import {
  GroupedSelectGroup,
  GroupedSelectOption,
} from '@/components/Form/GroupedSelect';

import { TIN_LENGTH } from '@/constants/common';

import {
  TaxEntryType,
  TaxInfoEntry,
  TaxInfoRequestPayload,
  TaxNumberTypeCode,
  UsTaxNoReturnReasonCode,
} from '@/types/api/tax-info';
import { TranslatorFunction } from '@/types/i18n';
import { CountryISO } from '@/types/places';

import { replaceOwnedTaxEntries } from '../utils/taxInfoStatus';
import { USTaxReturnFormValues } from '../utils/types';
import { US_TAX_NO_RETURN_REASON_CODES } from './constants';

function isUsTaxNumberTypeCode(
  code?: TaxNumberTypeCode,
): code is TaxNumberTypeCode.SSN | TaxNumberTypeCode.ITIN {
  return code === TaxNumberTypeCode.SSN || code === TaxNumberTypeCode.ITIN;
}

function isUsTaxNoReturnReasonCode(
  code?: UsTaxNoReturnReasonCode,
): code is UsTaxNoReturnReasonCode {
  return US_TAX_NO_RETURN_REASON_CODES.includes(
    code as (typeof US_TAX_NO_RETURN_REASON_CODES)[number],
  );
}

export function getUsTaxNoReturnReasonGroupedSelectConfig(
  t: TranslatorFunction,
): {
  groups: GroupedSelectGroup[];
  otherOption: GroupedSelectOption;
} {
  return {
    groups: [
      {
        options: US_TAX_NO_RETURN_REASON_CODES.filter(
          code => code !== UsTaxNoReturnReasonCode.OTHER,
        ).map(code => ({
          value: code,
          label: t(`taxInfoDrawer.question1.noReturnReason.options.${code}`),
        })),
      },
    ],
    otherOption: {
      value: UsTaxNoReturnReasonCode.OTHER,
      label: t(
        `taxInfoDrawer.question1.noReturnReason.options.${UsTaxNoReturnReasonCode.OTHER}`,
      ),
    },
  };
}

export function getUsTaxNumberMaxLength(taxNumberTypeCode?: TaxNumberTypeCode) {
  if (taxNumberTypeCode === TaxNumberTypeCode.ITIN) {
    return TIN_LENGTH.otherMax;
  }

  return TIN_LENGTH.usMax;
}

export function getUsTaxNumberMinLength(taxNumberTypeCode?: TaxNumberTypeCode) {
  if (taxNumberTypeCode === TaxNumberTypeCode.ITIN) {
    return TIN_LENGTH.otherMin;
  }

  return TIN_LENGTH.usMin;
}

export const DEFAULT_US_TAX_RETURN_FORM_VALUES: USTaxReturnFormValues = {
  usTaxReturnRequired: undefined,
  taxNumberTypeCode: undefined,
  usTaxNumber: '',
  usTaxNoReturnReasonCode: '',
  usTaxNoReturnReasonOther: '',
};

export function getUsTaxEntry(taxInfo?: TaxInfoEntry[]) {
  return taxInfo?.find(
    entry =>
      entry.taxEntryType === TaxEntryType.US_TAX &&
      entry.usTaxReturnRequired !== undefined,
  );
}

export function mapUsTaxEntryToFormValues(
  entry?: TaxInfoEntry,
): USTaxReturnFormValues {
  if (!entry) {
    return DEFAULT_US_TAX_RETURN_FORM_VALUES;
  }

  return {
    usTaxReturnRequired: entry.usTaxReturnRequired,
    taxNumberTypeCode: isUsTaxNumberTypeCode(entry.taxNumberTypeCode)
      ? entry.taxNumberTypeCode
      : undefined,
    usTaxNumber: entry.taxNumberValue ?? '',
    usTaxNoReturnReasonCode: isUsTaxNoReturnReasonCode(
      entry.usTaxNoReturnReasonCode,
    )
      ? entry.usTaxNoReturnReasonCode
      : '',
    usTaxNoReturnReasonOther: entry.usTaxNoReturnReasonOther ?? '',
  };
}

export function buildUsTaxReturnRequestPayload(
  formValues: USTaxReturnFormValues,
  existingTaxInfo?: TaxInfoEntry[],
): TaxInfoRequestPayload {
  let updatedEntry: TaxInfoEntry;

  if (formValues.usTaxReturnRequired) {
    updatedEntry = {
      countryCode: CountryISO.US,
      taxEntryType: TaxEntryType.US_TAX,
      isCompleted: true,
      usTaxReturnRequired: true,
      taxNumberTypeCode: formValues.taxNumberTypeCode,
      taxNumberValue: formValues.usTaxNumber,
    };
  } else {
    updatedEntry = {
      countryCode: CountryISO.US,
      taxEntryType: TaxEntryType.US_TAX,
      isCompleted: true,
      usTaxReturnRequired: false,
      usTaxNoReturnReasonCode: formValues.usTaxNoReturnReasonCode || undefined,
      ...(formValues.usTaxNoReturnReasonCode === UsTaxNoReturnReasonCode.OTHER
        ? { usTaxNoReturnReasonOther: formValues.usTaxNoReturnReasonOther }
        : {}),
    };
  }

  return replaceOwnedTaxEntries(
    existingTaxInfo ?? [],
    [updatedEntry],
    [TaxEntryType.US_TAX],
  );
}
